import React, { useState } from 'react';
import { useStringsContext } from '../../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Poll, PollVoter } from '../../types';
import BaseDialog from '../../../../common/base-dialog';
import DialogContent from '@mui/material/DialogContent';
import { usePollVoters } from '../../hooks/poll';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Collapse from '@mui/material/Collapse';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Loading from '../../../../loading';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { SxStyle } from '../../../../../types';

const styles: SxStyle = {
  listItem: (theme) => ({
    borderBottom: `1px solid ${theme.palette.grey[300]}`,

    '&:last-child': {
      borderBottom: 0,
    },
  }),
  listItemText: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px',
  },
  tableCell: {
    border: 0,
    paddingTop: '2px',
    paddingBottom: '2px',
  },
};

interface PollVoterItemProps {
  voter: PollVoter;
  index: number;
}

const PollVoterItem = ({ voter, index }: PollVoterItemProps) => {
  const { strings } = useStringsContext();
  const [showMore, setShowMore] = useState(false);

  return (
    <ListItem key={voter.personId} sx={styles.listItem} disablePadding>
      <Stack direction="column" flex={1}>
        <Stack direction="row" sx={styles.listItemText}>
          <ListItemText
            primaryTypographyProps={{ variant: 'body2' }}
            primary={strings.polls.votersVoteNumber + `${index + 1}`}
          />
          <Link
            sx={{ textDecoration: 'underline' }}
            component="button"
            variant="body2"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? strings.actions.hide : strings.actions.show}
          </Link>
        </Stack>
        <Collapse in={showMore}>
          <Table size="small" sx={{ marginBottom: '2px' }}>
            <TableBody>
              <TableRow>
                <TableCell sx={styles.tableCell} width={130} align="left">
                  <b>{strings.polls.votersName}</b>
                </TableCell>
                {voter.name && voter.name.trim().length > 0 ? (
                  <TableCell sx={styles.tableCell} align="left">
                    {voter.name}
                  </TableCell>
                ) : (
                  <TableCell sx={styles.tableCell} align="left">
                    <i>{strings.polls.votersNameMissing}</i>
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell sx={styles.tableCell} width={130} align="left">
                  <b>{strings.polls.votersEmail}</b>
                </TableCell>
                <TableCell sx={styles.tableCell} align="left">
                  <Link sx={{ textDecoration: 'underline' }} href={`mailto: ${voter.email}`}>
                    {voter.email}
                  </Link>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Collapse>
      </Stack>
    </ListItem>
  );
};

interface Props {
  open: boolean;
  selectedPoll: Poll;
  onClose: () => void;
}

const PollVotesModal = ({ open, selectedPoll, onClose }: Props) => {
  const { strings } = useStringsContext();
  const [selectedTab, setSelectedTab] = useState(0);

  // Use declarative hook for fetching voters
  const { data: voters, isLoading } = usePollVoters(selectedPoll?.id ?? null, {
    enabled: open && !!selectedPoll,
  });

  return (
    <BaseDialog open={open} title={strings.polls.votersTitle} onClose={onClose}>
      <DialogContent dividers sx={!isLoading ? { paddingTop: 0, paddingBottom: 0 } : {}}>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <Tabs value={selectedTab} onChange={(event, value) => setSelectedTab(value)}>
              <Tab label={strings.polls.votersTabWinners} />
              <Tab label={strings.polls.votersTabAll} />
            </Tabs>
            {selectedTab === 0 && (
              <List disablePadding>
                {voters
                  ?.filter((voter) => voter.votedOnWinner)
                  ?.map((voter, index) => (
                    <PollVoterItem key={voter.personId} index={index} voter={voter} />
                  ))}
              </List>
            )}
            {selectedTab === 1 && (
              <List disablePadding>
                {voters?.map((voter, index) => (
                  <PollVoterItem key={voter.personId} index={index} voter={voter} />
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={onClose}>
          {strings.actions.close}
        </Button>
      </DialogActions>
    </BaseDialog>
  );
};

export default PollVotesModal;
